import type { Dayjs } from 'dayjs';
import type { Application } from '../../../types';
import type { ClosingPackageProjection, RegisterScanDispatchBody } from '../types';
import { buildCompositionTableRows } from './compositionProjectionAdapter';
import {
  validateTaskModalEventDate,
} from './taskModalValidation';

export type SendScansValidationCode =
  | 'EVENT_DATE_REQUIRED'
  | 'PACKAGE_NOT_FORMED'
  | 'COMPOSITION_EMPTY'
  | 'REQUIRED_SCAN_MISSING';

export type SendScansValidationIssue = {
  code: SendScansValidationCode;
  message: string;
  itemLabel?: string;
};

export type ValidateSendScansFormInput = {
  eventAt: Dayjs | null | undefined;
  recipient?: string | null;
  comment?: string | null;
};

export function validateSendScansFormFields(
  input: ValidateSendScansFormInput
): SendScansValidationIssue | null {
  return validateTaskModalEventDate(
    input.eventAt,
    'Укажите дату отправки сканов'
  ) as SendScansValidationIssue | null;
}

export function validateSendScansComposition(
  projection: ClosingPackageProjection | null | undefined
): SendScansValidationIssue | null {
  if (!projection?.formed) {
    return {
      code: 'PACKAGE_NOT_FORMED',
      message: 'Комплект не сформирован. Сначала выполните формирование комплекта',
    };
  }

  const rows = buildCompositionTableRows(projection);
  if (rows.length === 0) {
    return {
      code: 'COMPOSITION_EMPTY',
      message: 'Состав комплекта пуст',
    };
  }

  for (const row of rows) {
    if (!row.required || row.item.compositionMeta?.isManual) {
      continue;
    }

    const linkedDocumentId = row.item.compositionMeta?.linkedDocumentId;
    if (linkedDocumentId == null || row.state === 'MISSING') {
      return {
        code: 'REQUIRED_SCAN_MISSING',
        message: `Отсутствует скан обязательного документа: ${row.documentLabel}`,
        itemLabel: row.documentLabel,
      };
    }
  }

  return null;
}

export function validateSendScansSubmit(input: {
  form: ValidateSendScansFormInput;
  projection: ClosingPackageProjection | null | undefined;
}): SendScansValidationIssue | null {
  return validateSendScansFormFields(input.form) ?? validateSendScansComposition(input.projection);
}

export function buildScanDispatchBody(input: {
  recipient?: string;
  comment?: string;
  dispatchedAt: string;
}): RegisterScanDispatchBody {
  const recipient = String(input.recipient ?? '').trim();
  const comment = String(input.comment ?? '').trim();

  return {
    dispatchMethod: 'EMAIL',
    ...(recipient ? { recipient } : {}),
    ...(comment ? { comment } : {}),
    dispatchedAt: input.dispatchedAt,
  };
}

export function buildDefaultScanDispatchRecipient(
  application: Application | null | undefined
): string {
  const client = application?.client as { email?: string | null } | undefined;
  return client?.email?.trim() ?? '';
}

export const SEND_SCANS_SUCCESS_WORKFLOW_STATE = 'WAITING_CLIENT_APPROVAL' as const;

export function isSendScansPostSubmitWorkflowState(state: string | null | undefined): boolean {
  return state === SEND_SCANS_SUCCESS_WORKFLOW_STATE || state === 'SCANS_SENT_TO_CLIENT';
}
